import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import AssetProcessingForm from "./forms/AssetProcessing/AssetProcessingForm";
import AssetStorageForm from "./forms/AssetStorage/AssetStorageForm";
import AssetSyndicationForm from "./forms/AssetSyndication/AssetSyndicationForm";
import ClientProcessForm from "./forms/ClientProcessForm";

interface ConfigDetailsProps {
  system: any;
  expandedConfigId: string | null;
  section: "clientProcess" | "assetProcessing" | "assetStorage" | "assetSyndication";
  onChange: (path: string, value: any) => void;
}

const sectionLabels: Record<ConfigDetailsProps["section"], string> = {
  clientProcess: "Client Process",
  assetProcessing: "Asset Processing",
  assetStorage: "Asset Storage",
  assetSyndication: "Asset Syndication",
};

const ConfigDetails: React.FC<ConfigDetailsProps> = ({
  system,
  expandedConfigId,
  section,
  onChange,
}) => {
  // ✅ always normalize configurations into an array
  const configs = useMemo(() => {
    if (!system?.configurations) return [];
    if (Array.isArray(system.configurations)) return system.configurations;

    // convert { configName: { ...configData } } → [{ name, ...configData }]
    return Object.entries(system.configurations).map(([name, value]) => ({
      name,
      ...(value as object),
    }));
  }, [system]);

  const selectedConfig = useMemo(
    () => configs.find((c: any) => c.name === expandedConfigId),
    [configs, expandedConfigId]
  );

  if (!selectedConfig) {
    return (
      <Typography variant="body1" sx={{ mt: 2 }}>
        No configuration selected.
      </Typography>
    );
  }

  const label = sectionLabels[section];
  const formData = selectedConfig?.[section] || {};
  const name =
    selectedConfig?.clientProcess?.name ||
    selectedConfig?.name ||
    "Unnamed Config";

  const handleFieldChange = (path: string, value: any) => {
    onChange(`configurations.${expandedConfigId}.${section}.${path}`, value);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {name}
      </Typography>
      <Typography variant="h6" sx={{ mb: 2, color: "text.secondary" }}>
        {label}
      </Typography>

      {section === "clientProcess" && (
        <ClientProcessForm config={formData} handleChange={handleFieldChange} />
      )}
      {section === "assetProcessing" && (
        <AssetProcessingForm config={formData} handleChange={handleFieldChange} />
      )}
      {section === "assetSyndication" && (
        <AssetSyndicationForm config={formData} handleChange={handleFieldChange} />
      )}
      {section === "assetStorage" && (
        <AssetStorageForm config={formData} handleChange={handleFieldChange}/>
      )}
    </Box>
  );
};

export default ConfigDetails;
